import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getTrialUpgradePrompt, recordTrialUpgradePromptOp, TrialCardQuote, TrialUpgradePromptResponse } from '@extension/wa-communication-layer/api/api-calls';
import { TrialCardConsent } from '@common/components/trial-card-return/trial-card-consent';
import { primaryTarget, shouldShowTrialUpgradePrompt } from './trial-upgrade-prompt-logic';

type ShowableResponse = TrialUpgradePromptResponse & {
  type: NonNullable<TrialUpgradePromptResponse['type']>;
  plan: NonNullable<TrialUpgradePromptResponse['plan']>;
  copy: NonNullable<TrialUpgradePromptResponse['copy']>;
};

/**
 * #1226 slice A: one dismissible in-trial upgrade popup. All copy comes from the server
 * (single source of truth in the backend). Both buttons are the same size and style, so
 * "Not now" carries equal weight to the primary action. Renders nothing unless the server
 * says show:true.
 */
export const TrialUpgradePrompt: React.FC = () => {
  const [prompt, setPrompt] = useState<ShowableResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [consentQuote, setConsentQuote] = useState<TrialCardQuote | null>(null);
  const fetchedRef = useRef(false);
  const shownRecordedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getTrialUpgradePrompt()
      .then((res) => {
        if (shouldShowTrialUpgradePrompt(res)) {
          setPrompt(res);
          setOpen(true);
        }
      })
      .catch(() => { /* a failed decision check never blocks the app: no popup */ });
  }, []);

  // Record `shown` once, when the popup actually appears (server validates the arm).
  useEffect(() => {
    if (!open || !prompt || shownRecordedRef.current) return;
    shownRecordedRef.current = true;
    recordTrialUpgradePromptOp('shown', prompt.type).catch(() => {});
  }, [open, prompt]);

  if (!prompt) return null;

  const close = (): void => setOpen(false);

  const onPrimary = (): void => {
    recordTrialUpgradePromptOp('clicked', prompt.type).catch(() => {});
    close();
    // #1229: the server sends the popup copy and the card quote from ONE check. With a quote the CTA opens the
    // no-charge consent and never the charging checkout; without one it is the normal checkout, as before.
    const target = primaryTarget(prompt);
    if (target.kind === 'consent') setConsentQuote(target.quote);
    else window.open(target.url, '_blank', 'noopener');
  };

  const onNotNow = (): void => {
    recordTrialUpgradePromptOp('dismissed', prompt.type).catch(() => {});
    close();
  };

  // Equal weight: identical variant, size and class for both buttons.
  const equalButtonClass = 'min-w-[9rem] flex-1 h-auto whitespace-normal py-2';

  return (
    <>
    <Dialog open={open} onOpenChange={(next) => { if (!next) onNotNow(); }}>
      <DialogContent className="max-w-md" data-testid="trial-upgrade-prompt">
        <DialogHeader>
          <DialogTitle>{prompt.copy.heading}</DialogTitle>
          <DialogDescription>{prompt.copy.body}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{prompt.copy.footer}</p>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className={equalButtonClass} onClick={onNotNow}>
            {prompt.copy.notNow}
          </Button>
          <Button variant="outline" className={equalButtonClass} onClick={onPrimary}>
            {prompt.copy.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {consentQuote !== null && (
      <TrialCardConsent
        quote={consentQuote}
        notNowLabel={prompt.copy.notNow}
        onCancel={() => setConsentQuote(null)}
      />
    )}
    </>
  );
};
